import { useEffect, useState } from "react";

import { SdTestAppBenchClient, type BenchDocumentationPayload } from "@/lib/sdtestapp/sdTestAppBenchClient";

export function BenchDocumentationTab({
  port,
  machineId,
  observeOnly = false,
}: {
  port?: string;
  machineId?: string;
  observeOnly?: boolean;
}): JSX.Element {
  const [payload, setPayload] = useState<BenchDocumentationPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        if (observeOnly && machineId) {
          const next = await SdTestAppBenchClient.relayJson<BenchDocumentationPayload>(
            machineId,
            "/api/hub/v1/documentation",
          );
          setPayload(next);
          return;
        }
        const client = new SdTestAppBenchClient(port);
        const next = await client.fetchDocumentation();
        setPayload(next);
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : String(loadError));
      }
    })();
  }, [machineId, observeOnly, port]);

  if (error) {
    return <p className="text-sm text-destructive">Could not load documentation: {error}</p>;
  }
  if (!payload) {
    return <p className="text-sm text-muted-foreground">Loading documentation…</p>;
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{payload.intro}</p>
      {payload.rules.map((rule) => (
        <article key={rule.appliesTo} className="rounded border p-4">
          <h3 className="font-semibold">
            {rule.appliesTo} · {rule.pattern}
          </h3>
          <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
            <div>
              <dt className="font-medium text-red-700">Before</dt>
              <dd className="font-mono">{rule.fromExample}</dd>
            </div>
            <div>
              <dt className="font-medium text-emerald-700">After</dt>
              <dd className="font-mono">{rule.toExample}</dd>
            </div>
          </dl>
          <p className="mt-3 text-sm">{rule.explanation}</p>
          {rule.importantNotes?.length ? (
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
              {rule.importantNotes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          ) : null}
        </article>
      ))}
    </div>
  );
}
